import { BridgeClient } from 'app/service/bridge/BridgeClient';

function normalizeBridgeField(field) {
  return {
    id: field.id,
    title: field.title,
    weight: field.weight,
    // Bridge fields are weight-only in plugin admin. Null keeps the UI from
    // rendering autocomplete controls while preserving legacy screen behavior.
    autocomplete: null,
  };
}

/**
 * Creates a fields API backed by the bridge transport (postMessage → parent → plugin backend).
 *
 * Bridge actions used:
 *   fields.list        → BridgeUseCase → CanonicalFieldCustomizationService.getFields()
 *   fields.update_bulk → BridgeUseCase → CanonicalFieldCustomizationService.updateFields()
 *
 * Bridge list response shape (canonical):
 *   { result, data: { document_type, fields: [{id, weight, title,
 *     autocomplete, keyword, searchResultData, inversed, phonetics,
 *     isSystemField}] } }
 *
 * Note: `document_type` is currently hardcoded to 'product'. Multi-type support
 * can be added later by passing documentType as a parameter to load()/save().
 */
export function createBridgeFieldsApi() {
  const client = new BridgeClient();

  return {
    /**
     * @param {string} _shopId — not used by bridge (context from plugin side), kept for interface parity
     * @returns {Promise<{ fields: Array<{id, title, weight, autocomplete}> }>}
     */
    async load() {
      const response = await client.sendMessageWithResponse('fields.list', {
        document_type: 'product',
      });

      // response.data: { document_type, fields: [{id, weight, title, autocomplete, ...}] }
      const rawFields = (response.data && response.data.fields) || [];
      const fields = rawFields.map(normalizeBridgeField);

      return { fields };
    },

    /**
     * @param {string} _shopId — not used by bridge
     * @param {Array<{id, title, weight, autocomplete}>} fields
     * @returns {Promise<{ fields: Array }>}
     */
    async save(_shopId, fields) {
      await client.sendMessageWithResponse('fields.update_bulk', {
        document_type: 'product',
        fields: fields.map((field) => ({ id: field.id, weight: field.weight })),
        enqueue_reindex: false,
      });

      // Re-fetch to get the authoritative post-save state
      return this.load(_shopId);
    },
  };
}
